/**
 * Singleton loader for the Google Maps JavaScript API.
 *
 * `external-sharing` Phase 6 Session 6.3 (2026-05-09).
 * `conversation-model-versioning` Phase 6 Session 1 (2026-05-18) —
 * added `loadPlaces()` sibling entry (ADR 0022).
 *
 * Two reasons for centralizing the load:
 * 1. The Maps SDK is ~80KB minified — we want it loaded ONCE per session,
 *    not per `<PropertyMap>` mount. The `Loader` from `@googlemaps/js-api-loader`
 *    handles that internally, but only if the same instance is reused;
 *    instantiating multiple `Loader`s with different `apiKey` values
 *    triggers a console warning + silent fallback.
 * 2. The build-time env vars (`VITE_GOOGLE_MAPS_API_KEY` + `VITE_GOOGLE_MAPS_MAP_ID`)
 *    are validated here once. If either is missing the loader rejects with a
 *    typed error — consumers can render the API-key-missing empty state without
 *    triggering a Google CDN request.
 *
 * `loadPlaces()` validates ONLY `VITE_GOOGLE_MAPS_API_KEY` (ADR 0022 — the
 * Places Autocomplete path does not require a Map ID). It shares the same
 * `optionsApplied` guard as `loadMaps()` so `setOptions` is called exactly once
 * regardless of which entry point fires first.
 *
 * GPL3 audit safety: this file imports `@googlemaps/js-api-loader` which is
 * Apache 2.0 (verified package.json). The SDK fetched by the loader is
 * proprietary but client-fetched directly from Google's CDN — never bundled
 * by Vite. `scripts/audit-no-hyperformula-in-web.sh` (Phase 2) does NOT
 * flag this surface.
 */
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';

export class GoogleMapsConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GoogleMapsConfigError';
  }
}

export interface MapsRuntime {
  Map: typeof google.maps.Map;
  AdvancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement;
  mapId: string;
}

 
let optionsApplied = false;
 
let cachedRuntime: Promise<MapsRuntime> | null = null;
 
let cachedPlacesRuntime: Promise<typeof google.maps.places> | null = null;

/**
 * Resolve the Maps SDK runtime. Loads the SDK on first call; subsequent calls
 * reuse the cached promise (so concurrent mounts share one fetch).
 *
 * Uses the v2 functional API (`setOptions()` + `importLibrary()`); the
 * Loader class is deprecated as of v2.0 of `@googlemaps/js-api-loader`.
 *
 * @throws GoogleMapsConfigError when env vars are missing.
 */
export function loadMaps(): Promise<MapsRuntime> {
  if (cachedRuntime) return cachedRuntime;

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const mapId = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID;
  if (typeof apiKey !== 'string' || apiKey.length === 0) {
    return Promise.reject(
      new GoogleMapsConfigError(
        'VITE_GOOGLE_MAPS_API_KEY is missing — set it in apps/web/.env.local for dev, or via SST secrets for deployed stages.'
      )
    );
  }
  if (typeof mapId !== 'string' || mapId.length === 0) {
    return Promise.reject(
      new GoogleMapsConfigError(
        'VITE_GOOGLE_MAPS_MAP_ID is missing — create a Cloud Map ID in GCP Console (Maps Management → Map Styles → New Style) and set it in env.'
      )
    );
  }

  if (!optionsApplied) {
    setOptions({ key: apiKey, v: 'weekly' });
    optionsApplied = true;
  }

  cachedRuntime = (async () => {
    const [mapsLib, markerLib] = await Promise.all([
      importLibrary('maps'),
      importLibrary('marker'),
    ]);
    return {
      Map: mapsLib.Map,
      AdvancedMarkerElement: markerLib.AdvancedMarkerElement,
      mapId,
    };
  })();

  return cachedRuntime;
}

/**
 * Resolve the Places library. Places-only path for `<LocationInput>` — does NOT
 * require `VITE_GOOGLE_MAPS_MAP_ID` (ADR 0022 — map display is a separate concern).
 *
 * Shares the module-level `optionsApplied` guard with `loadMaps()` so
 * `setOptions` is called at most once regardless of which entry fires first.
 * Memoises its own `cachedPlacesRuntime` promise independently of `cachedRuntime`.
 *
 * @throws GoogleMapsConfigError when VITE_GOOGLE_MAPS_API_KEY is missing.
 */
export function loadPlaces(): Promise<typeof google.maps.places> {
  if (cachedPlacesRuntime) return cachedPlacesRuntime;

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  if (typeof apiKey !== 'string' || apiKey.length === 0) {
    return Promise.reject(
      new GoogleMapsConfigError(
        'VITE_GOOGLE_MAPS_API_KEY is missing — set it in apps/web/.env.local for dev, or via SST secrets for deployed stages.'
      )
    );
  }

  if (!optionsApplied) {
    setOptions({ key: apiKey, v: 'weekly' });
    optionsApplied = true;
  }

  cachedPlacesRuntime = importLibrary('places') as Promise<
    typeof google.maps.places
  >;

  return cachedPlacesRuntime;
}

/**
 * Test-only escape hatch — clears the singleton so each test starts cold.
 * Intended for vitest setup, not production code paths.
 */
export function __resetMapsLoaderForTests(): void {
  optionsApplied = false;
  cachedRuntime = null;
  cachedPlacesRuntime = null;
}
